import { FC } from 'react';
import ImageIconSolid from '@/components/icons/ImageIconSolid';
import PackageAddIcon from '@/components/icons/PackageAddIcon';
import { Button } from '@/components/ui/button';
import ApartmentsCardChart from '@/features/apartments/components/ApartmentsCard/ApartmentsCardChart';
import { GetApartmentListItemResponseDto } from '@/features/apartments/types/apartments-dto';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import ECarousel from '@/components/everridge/carousel/ECarousel';
import ApartmentsCardField from '@/features/apartments/components/ApartmentsCard/ApartmentsCardField';
import CalendarAddIcon from '@/components/icons/CalendarAddIcon';
import User_02_Icon from '@/components/icons/User_02_Icon';
import { cn } from '@/lib/utils';
import { t } from 'i18next';
import { Roles } from '@/@types/auth.types.ts';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

type Props = {
	showDetails: (id: string) => void;
	setNewReservation?: (id: string) => void;
	setNewOrder?: (id: string, name?: string) => void;
	footerButtons?: Roles;
} & GetApartmentListItemResponseDto;

const ApartmentsCard: FC<Props> = ({
	id,
	name,
	ownerName,
	description,
	cadastralCode,
	squareness,
	propertyBalance,
	otherServicesBalance,
	usedDays,
	maxDays,
	photoUrls,
	showDetails,
	setNewReservation,
	setNewOrder,
	footerButtons,
}) => {
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { ordersEnabled } = useMultiCurrencyStorage();
	const canPlaceOrders = ordersEnabled && !!permissions?.placeOrders;

	const ownerButtons = (
		<>
			<Button
				variant='outline'
				className='relative min-w-28 flex-1'
				onClick={() => setNewReservation?.(id)}
				disabled={!permissions?.createBookings}>
				<CalendarAddIcon />
				{t('elements.buttons.reserve')}
			</Button>
			<Button
				variant='outline'
				className='relative min-w-28 flex-1'
				onClick={() => setNewOrder?.(id, name)}
				disabled={!canPlaceOrders}>
				<PackageAddIcon />
				{t('elements.buttons.order')}
			</Button>
		</>
	);

	const adminButtons = (
		<>
			<User_02_Icon />
			<span className='text-sm text-popover-1000'>
				{t('main.apartments.components.card.buttons.owner')}
			</span>
			<span className='text-sm text-popover-1000 ml-auto truncate'>
				{ownerName ??
					t('main.apartments.components.card.buttons.noOwner')}
			</span>
		</>
	);

	return (
		<div className='relative h-fit flex flex-col flex-1 border border-popover-100 rounded-2.5xl bg-popover-10 overflow-hidden hover:shadow-custom cursor-pointer transition-shadow duration-300'>
			<div
				className='absolute inset-0'
				onClick={() => showDetails(id)}
			/>
			<div onClick={() => showDetails(id)}>
				<ECarousel containerClassName='h-50'>
					{photoUrls?.length ? (
						photoUrls?.map((url, index) => (
							<EImageWithFallback
								key={index}
								src={url}
								fallbackSVG={ImageIconSolid}
							/>
						))
					) : (
						<EImageWithFallback fallbackSVG={ImageIconSolid} />
					)}
				</ECarousel>
			</div>
			<div className='p-6 flex flex-col gap-4'>
				<div className='flex items-center justify-between gap-4 truncate'>
					<div className='flex flex-col gap-1 truncate'>
						<h4 className='text-lg font-medium'>{name}</h4>
						<p className='text-base text-popover-800 truncate'>
							{description ??
								t(
									'main.apartments.components.card.noDescription',
								)}
						</p>
					</div>
					<ApartmentsCardChart
						usedDays={usedDays}
						maxDays={maxDays}
					/>
				</div>
				<div className='flex flex-col gap-4'>
					<ApartmentsCardField
						property='Cadastral code'
						value={`${cadastralCode}`}
						variant={'desktop'}
					/>
					<ApartmentsCardField
						property='Squareness'
						value={`${squareness}`}
						variant={'desktop'}
					/>
					<ApartmentsCardField
						property='Property'
						value={<CurrencyValue value={propertyBalance} asset='property' />}
						variant={'desktop'}
					/>
					<ApartmentsCardField
						property='OtherServices'
						value={<CurrencyValue value={otherServicesBalance} asset='otherservices' />}
						variant={'desktop'}
					/>
				</div>
			</div>
			{!!footerButtons && (
				<div
					className={cn(
						'p-6 flex items-center border-t border-popover-100',
						footerButtons === Roles.owner ? 'gap-4' : 'gap-2.5',
					)}>
					{footerButtons === Roles.owner && ownerButtons}
					{footerButtons === Roles.admin && adminButtons}
				</div>
			)}
		</div>
	);
};

export default ApartmentsCard;
